import React, { useMemo } from 'react';
import { Select } from 'antd';
import { Form } from '@common/components';
import { selectInputFilterHelper } from '@common/services';
import { RoleType, RoleFlatType } from '@clinician-profile/graphql';

type RoleSelectProps = {
  disabled?: boolean;
  loading?: boolean;
  roles?: RoleType[] | RoleFlatType[];
  showLabel?: boolean;
  labelKey?: 'abbreviation' | 'name';
  placeholder?: string;
  size?: 'large' | 'middle' | 'small';
};

export function RoleSelect({
  roles,
  disabled = false,
  showLabel = true,
  labelKey = 'abbreviation',
  placeholder = 'Select role',
  size = 'large',
  ...props
}: RoleSelectProps) {
  const { suggestedRoles, otherRoles } = useMemo(() => {
    if (!roles) return { suggestedRoles: [], otherRoles: [] };

    const suggested = roles.filter((role) => role.suggested);
    const suggestedIds = new Set(suggested.map((r) => r.id));
    const others = roles.filter((role) => !suggestedIds.has(role.id));

    return {
      suggestedRoles: [...suggested].sort((a, b) =>
        a[labelKey]?.localeCompare(b[labelKey])
      ),
      otherRoles: [...others].sort((a, b) =>
        a[labelKey]?.localeCompare(b[labelKey])
      ),
    };
  }, [roles, labelKey]);

  const hasSuggestions = suggestedRoles.length > 0;

  const renderOption = (role: RoleType | RoleFlatType) => {
    const label = role[labelKey];
    return (
      <Select.Option key={role.id} value={role.id} label={label}>
        {label}
      </Select.Option>
    );
  };

  return (
    <Form.Item
      name="roleId"
      label={showLabel && 'Role'}
      colon={false}
      rules={[{ required: true, message: 'Please select a role' }]}
    >
      <Select
        showSearch
        size={size}
        disabled={disabled}
        placeholder={placeholder}
        filterOption={selectInputFilterHelper}
        getPopupContainer={(trigger) => trigger.parentNode}
        {...props}
      >
        {hasSuggestions ? (
          <>
            <Select.OptGroup label="Suggested">
              {suggestedRoles.map(renderOption)}
            </Select.OptGroup>
            {otherRoles.length > 0 && (
              <Select.OptGroup label="Other Roles">
                {otherRoles.map(renderOption)}
              </Select.OptGroup>
            )}
          </>
        ) : (
          roles?.map(renderOption)
        )}
      </Select>
    </Form.Item>
  );
}
